import { useEffect, useRef, useState } from 'react';
import { Loader2, Car, LocateFixed, Fuel, ShoppingCart } from 'lucide-react';
import useGpsPoloha from './hooks/useGpsPoloha';
import { AKCENT, CHYBA, OK, hm, plocha, Nadpis, Zpet, tint } from './ui';

/**
 * V autě — naloženo, čeká se na Vyjíždím (2026-10-29).
 *
 * „Naloženo" u regálu jízdu nespouští: odpočet, SMS zákazníkům a navigace
 * začnou až tímhle tuknutím, když rozvozce sedí v autě. Celá spodní půlka
 * je jedno tlačítko, ať jde trefit z místa řidiče.
 *
 * Pojistka: když telefon (nebo tracker v autě na serveru) ukáže, že auto
 * je dál od bistra než `auto_vyjezd_m`, jízda se spustí sama.
 */
export default function Vyjezd({ data, onRefresh, onZpet, isDark }) {
    const davka = data.davka;
    const zastavky = (data.zastavky ?? []).filter(z => (z.stav_doruceni ?? 'ceka') === 'ceka');
    const mista = data.mista ?? [];
    const prvni = zastavky[0] ?? null;
    const [busy, setBusy] = useState(null);
    const [err, setErr] = useState(null);
    const { poloha } = useGpsPoloha({ enabled: true });
    const posledniAuto = useRef(0);

    const post = async (url, body, klic) => {
        setBusy(klic);
        setErr(null);
        try {
            const r = await fetch(url, {
                method: 'POST', credentials: 'same-origin',
                headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
                body: JSON.stringify(body ?? {}),
            });
            const json = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(json.error || `HTTP ${r.status}`);
            await onRefresh();
        } catch (e) {
            setErr(e?.message || String(e));
        } finally {
            setBusy(null);
        }
    };

    const vyjizdim = () => post(`/api/rozvoz/davky/${davka.id}/spustit`, {}, 'vyjizdim');
    const zpetKeKontrole = () => post(`/api/rozvoz/davky/${davka.id}/nalozit`, { zpet: true }, 'zpet');

    // Pojistka — auto odjelo od bistra. Server rozhodne podle vzdálenosti,
    // telefon se ptá nejvýš jednou za 15 s a jen když je zjevně daleko.
    const bistro = data.bistro;
    const prah = davka.auto_vyjezd_m ?? 200;
    const daleko = poloha && bistro?.lat ? vzdalenostM(poloha, bistro) : null;
    useEffect(() => {
        if (daleko == null || daleko < prah || busy) return;
        if (Date.now() - posledniAuto.current < 15000) return;
        posledniAuto.current = Date.now();
        post(`/api/rozvoz/davky/${davka.id}/spustit`, { auto: true, lat: poloha.lat, lng: poloha.lng }, 'auto');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [daleko]);

    const pocet = zastavky.length;
    return (
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 14, padding: 'calc(12px + env(safe-area-inset-top, 0px)) 16px calc(16px + env(safe-area-inset-bottom, 0px))' }}>
            <div><Zpet onClick={onZpet} isDark={isDark} /></div>

            <div>
                <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text-secondary)' }}>
                    Naloženo · {pocet} {pocet === 1 ? 'objednávka' : pocet < 5 ? 'objednávky' : 'objednávek'}
                    {mista.length > 0 && ` · ${mista.length} ${mista.length === 1 ? 'zastávka' : 'zastávky'}`}
                </div>
                <h1 style={{ margin: '2px 0 0', fontFamily: 'var(--pismo-display)', fontWeight: 400, fontSize: 30, lineHeight: 1.05, textTransform: 'uppercase' }}>
                    Až budeš v autě, klepni
                </h1>
            </div>

            {prvni && (
                <div style={{ ...plocha({ zaobleni: 18 }), padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <Nadpis style={{ fontSize: 11 }}>První zastávka</Nadpis>
                    <div style={{ fontSize: 18, fontWeight: 900, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        #{prvni.cislo_denni ?? prvni.cislo_objednavky} · {prvni.adresa || '—'}
                    </div>
                    <div style={{ fontSize: 13.5, color: 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums' }}>
                        {prvni.cas_slibeny ? `slíbeno ${String(prvni.cas_slibeny).slice(0, 5)}` : 'bez slibu'}
                        {davka.cas_navratu_predpoklad && ` · zpět kolem ${hm(davka.cas_navratu_predpoklad)}`}
                    </div>
                    {mista.length > 0 && (
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
                            {mista.map(m => {
                                const Ik = m.druh === 'obchod' ? ShoppingCart : Fuel;
                                return (
                                    <span key={m.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12.5, fontWeight: 700, padding: '3px 9px', borderRadius: 999, background: 'var(--surface-elevated)' }}>
                                        <Ik size={13} />{m.nazev} · {m.pobyt_min} min
                                    </span>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {err && <div style={{ fontSize: 13.5, fontWeight: 700, color: CHYBA }}>{err}</div>}

            <button type="button" onClick={vyjizdim} disabled={!!busy} style={{
                flex: 1, minHeight: 220, marginTop: 'auto', borderRadius: 28, border: 'none', cursor: 'pointer',
                background: AKCENT, color: '#16181b', font: 'inherit',
                boxShadow: `0 0 0 6px ${tint(AKCENT, 0.25)}`,
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8,
                WebkitTapHighlightColor: 'transparent',
            }}>
                {busy === 'vyjizdim' || busy === 'auto'
                    ? <Loader2 size={40} className="animate-spin" />
                    : <Car size={40} strokeWidth={2.4} />}
                <span style={{ fontFamily: 'var(--pismo-display)', fontSize: 40, lineHeight: 1, textTransform: 'uppercase' }}>Vyjíždím</span>
                <span style={{ fontSize: 13.5, fontWeight: 700, opacity: 0.75 }}>otevře navigaci na první zastávku</span>
            </button>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: 12.5, fontWeight: 600, color: 'var(--text-secondary)', textAlign: 'center' }}>
                <LocateFixed size={14} style={{ color: daleko != null ? OK : 'var(--text-muted)', flexShrink: 0 }} />
                Když zapomeneš, jízda se spustí sama {prah} m od bistra
            </div>
            <button type="button" onClick={zpetKeKontrole} disabled={!!busy} style={{
                border: 'none', background: 'transparent', font: 'inherit', cursor: 'pointer',
                fontSize: 14, fontWeight: 700, color: 'var(--text-secondary)', textDecoration: 'underline', textUnderlineOffset: 3, padding: 6,
            }}>
                {busy === 'zpet' ? 'vracím…' : 'zpět ke kontrole'}
            </button>
        </div>
    );
}

function vzdalenostM(a, b) {
    const R = 6371000;
    const r = (x) => (x * Math.PI) / 180;
    const dLat = r(b.lat - a.lat);
    const dLng = r(b.lng - a.lng);
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(r(a.lat)) * Math.cos(r(b.lat)) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(h));
}
